// DepartmentHull — stateless SVG primitive that draws a semi-transparent
// convex hull around the nodes belonging to a department.
//
// The hull is computed as a Minkowski-sum approximation: every node centre
// contributes `NODE_SAMPLE_COUNT` points sampled around a `HULL_PADDING`
// circle, and d3-polygon's `polygonHull` is run over that cloud. This
// guarantees the resulting polygon encloses each node's rendered disc even
// at acute hull corners, which a centroid-radial offset of the raw-centre
// hull cannot promise.
//
// `polygonHull` returns null for <3 points and for fully collinear input.
// In both cases we fall back to a circle around the centroid (radius = max
// distance from centroid + padding). The department label sits at the
// hull's top-left corner — the centroid often collides with nodes,
// especially for two-person clusters.

import { polygonHull } from 'd3-polygon';

const FALLBACK_PADDING = 28;
/** Outward padding applied around each node so the hull encloses the
 * rendered PersonNode circles rather than tracing through their centres.
 * Matches PersonNode's regular radius (24) plus a small visual margin. */
const HULL_PADDING = 28;
/** Samples per node when building the Minkowski-sum point cloud. 6 samples
 * (every 60°) gives a hull that visually clears each node's circumference
 * without exploding the input size to the convex-hull routine. */
const NODE_SAMPLE_COUNT = 6;
const LABEL_OFFSET = 6;
const SHARED_STYLE = {
  fillOpacity: 0.08,
  strokeOpacity: 0.55,
  strokeWidth: 2,
  labelOpacity: 0.8,
} as const;

interface Point {
  x: number;
  y: number;
}

interface DepartmentHullProps {
  /** Department name (rendered as the label). */
  name: string;
  /** Logical coordinates of the department's members. */
  points: Point[];
  /** Hull stroke / fill colour (CSS variable or hex). US6 owns the map. */
  color?: string;
}

function centroidOf(points: Point[]): Point {
  const sum = points.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
    { x: 0, y: 0 },
  );
  return { x: sum.x / points.length, y: sum.y / points.length };
}

function maxDistanceFrom(centre: Point, points: Point[]): number {
  return points.reduce((max, p) => {
    const dx = p.x - centre.x;
    const dy = p.y - centre.y;
    return Math.max(max, Math.hypot(dx, dy));
  }, 0);
}

function hullTopLeftLabel(hull: [number, number][]): Point {
  const minX = Math.min(...hull.map(([x]) => x));
  const minY = Math.min(...hull.map(([, y]) => y));
  return { x: minX, y: minY - LABEL_OFFSET };
}

/**
 * Build the Minkowski-sum point cloud for inflating the hull: sample a
 * `padding`-radius circle around every input node at {@link NODE_SAMPLE_COUNT}
 * angles. The convex hull of the resulting cloud is guaranteed to enclose
 * each node's full circle (modulo the discretisation error of finite
 * samples), which a centroid-radial offset cannot promise at sharp corners.
 */
function nodeCircleSamples(points: Point[], padding: number): [number, number][] {
  const cloud: [number, number][] = [];
  for (const p of points) {
    for (let i = 0; i < NODE_SAMPLE_COUNT; i++) {
      const theta = (i / NODE_SAMPLE_COUNT) * Math.PI * 2;
      cloud.push([p.x + Math.cos(theta) * padding, p.y + Math.sin(theta) * padding]);
    }
  }
  return cloud;
}

/** Convert a d3 hull (array of [x, y] tuples) into an SVG path string. */
function hullPath(hull: [number, number][]): string {
  return hull.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ') + ' Z';
}

/** Shared label element — keeps presentation consistent across both shapes. */
function HullLabel({ x, y, color, name }: Readonly<{ x: number; y: number; color: string; name: string }>) {
  return (
    <text
      x={x}
      y={y}
      fill={color}
      fillOpacity={SHARED_STYLE.labelOpacity}
      className="select-none text-sm font-semibold"
    >
      {name}
    </text>
  );
}

function CircleFallback({
  points,
  color,
  name,
}: Readonly<{ points: Point[]; color: string; name: string }>) {
  const centre = centroidOf(points);
  const radius = maxDistanceFrom(centre, points) + FALLBACK_PADDING;
  return (
    <g data-testid="department-hull" data-fallback="circle">
      <circle
        cx={centre.x}
        cy={centre.y}
        r={radius}
        fill={color}
        fillOpacity={SHARED_STYLE.fillOpacity}
        stroke={color}
        strokeOpacity={SHARED_STYLE.strokeOpacity}
        strokeWidth={SHARED_STYLE.strokeWidth}
      />
      <HullLabel x={centre.x - radius} y={centre.y - radius - LABEL_OFFSET} color={color} name={name} />
    </g>
  );
}

export function DepartmentHull({ name, points, color = 'var(--color-muted)' }: Readonly<DepartmentHullProps>) {
  if (points.length === 0) return null;

  // <3 points → convex hull undefined; fall back to a circle.
  if (points.length < 3) {
    return <CircleFallback points={points} color={color} name={name} />;
  }

  // ≥3 points: hull of the Minkowski-inflated sample cloud (a `HULL_PADDING`
  // ring of points around every node). This guarantees the resulting polygon
  // encloses each node's circle even at acute corners — a centroid-radial
  // offset of the raw-centre hull leaves gaps there. d3-polygon may still
  // return null for fully collinear input, so guard the result.
  const cloud = nodeCircleSamples(points, HULL_PADDING);
  const hull = polygonHull(cloud);
  if (hull === null || hull.length < 3) {
    return <CircleFallback points={points} color={color} name={name} />;
  }

  const label = hullTopLeftLabel(hull);
  return (
    <g data-testid="department-hull" data-fallback="hull">
      <path
        d={hullPath(hull)}
        fill={color}
        fillOpacity={SHARED_STYLE.fillOpacity}
        stroke={color}
        strokeOpacity={SHARED_STYLE.strokeOpacity}
        strokeWidth={SHARED_STYLE.strokeWidth}
      />
      <HullLabel x={label.x} y={label.y} color={color} name={name} />
    </g>
  );
}
